// ABOUTME: The back action of a custom page detail widget: to the page it was opened from, else the page's list,
// ABOUTME: or, inside an embedding view (the PDP dialog), that view's own onBack; hidden by hideBackButton or an embedding view without one.

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCustomPageOrigin } from '../../context/CustomPageOriginContext';
import { SKIP_AUTO_OPEN_STATE } from '../types';
import { readNavigationOrigin } from '../utils/navigationOrigin';

export interface CustomPageBackAction {
    /** Whether the widget shows its back button: not hidden by config, and with somewhere to go. */
    visible: boolean;
    goBack: () => void;
    /** Name of the page the detail was opened from (see `useOpenNode`); undefined otherwise. */
    originLabel?: string;
}

/** `hideBackButton` is the widget's own config flag, shared by node-info and node-editor. */
export function useCustomPageBackAction(
    slug: string | undefined,
    hideBackButton: boolean,
): CustomPageBackAction {
    const navigate = useNavigate();
    const location = useLocation();
    const { embedded, onBack } = useCustomPageOrigin();
    const origin = readNavigationOrigin(location.state);
    const goBack = useCallback(() => {
        // Going back to the page's list would leave the embedding view, so it handles the back.
        if (embedded && onBack) {
            onBack();
            return;
        }
        // A detail opened from another page returns there, with that page's own state (and so its
        // own origin), else to the page's own list. The target may be a list that opens its single
        // result by itself, which must not bounce back.
        navigate(origin?.path ?? `/pages/${slug}`, {
            state: { ...origin?.state, ...SKIP_AUTO_OPEN_STATE },
        });
    }, [embedded, onBack, navigate, origin?.path, origin?.state, slug]);
    // Inside an embedding view without its own back action there is nowhere to go back to.
    return {
        visible: !hideBackButton && (!embedded || !!onBack),
        goBack,
        originLabel: embedded ? undefined : origin?.label || undefined,
    };
}
